import { ReactNode, forwardRef, useContext, useMemo } from "react";
import { Link as RouterLink, LinkProps as RouterLinkProps } from "react-router";

import { LinkClickEventHandler, Tooltip } from "capitalrx-components-2.0/lib/general";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";

import { BaseProps } from "../../BaseProps";
import { BodyContext, BodyContextValue } from "../../typography/Body/BodyContext";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { LinkColor } from "./LinkColor";
import { LinkSize } from "./LinkSize";
import { isExternal } from "./isExternal";

import tokenStyles from "./styles.module.css";

const useResolvedLinkStyles = createComponentStyleHook<LinkProps>({
  legacyStyles: () => import("./legacy/styles.module.scss"),
  tokenStyles,
  componentTokens: "link",
});

/**
 * The `Link` component properties.
 */
export interface LinkProps extends BaseProps {
  /**
   * Specifies the URL to navigate to. For more information please visit https://v5.reactrouter.com/web/api/Link/to-string
   */
  readonly to: RouterLinkProps["to"];

  /**
   * When true, clicking the link will replace the current entry in the history stack instead of adding a new one. for more information please visit https://v5.reactrouter.com/web/api/Link/replace-bool
   */
  readonly replace?: RouterLinkProps["replace"];

  /**
   * When "route," resolves the link relative to the route pattern. When "path," relative to the current path.
   */
  readonly relative?: RouterLinkProps["relative"];

  /**
   * The tab index.
   */
  readonly tabIndex?: number;

  /**
   * The tooltip for the element.
   */
  readonly tooltip?: ReactNode;

  /**
   * The children for the element.
   */
  readonly children: ReactNode;

  /**
   * The Link color. If not specified default to `"turquoise"`.
   */
  readonly color?: LinkColor;

  /**
   * A function to run when clicking on the link
   */
  readonly onClick?: LinkClickEventHandler;

  /**
   * Set link to open in a new tab for internal links
   */
  readonly openInNewTab?: boolean;

  /**
   * Optional aria-label to provide an accessible name for the link. When provided this value will be
   * used instead of any computed label.
   */
  readonly "aria-label"?: string;

  /**
   * Specifies the size of the link text. If not specified defaults to "small". If set to "inherit" then the
   * body element will inherit the size of the first parent body element.
   */
  readonly size?: LinkSize;

  /**
   * When true, clicking the link will trigger a full page reload instead of client-side navigation.
   */
  readonly reloadDocument?: boolean;
}

/**
 * The link component should be used to navigate through the app or navigating to an external page.
 * This component is using the `Link` component exported from `react-router`.
 *
 * see documentation here <a href="https://v5.reactrouter.com/web/api/Link">React Router</a>.
 *
 * **Note: An external link is determined by the following properties,
 * If your url starts with `http` or `https` it is considered an external link.
 *
 * All external links will be opened in a new tab.**
 *
 * **External links will have the following properties added.**
 *
 * ```
 *  - target="_blank"
  - rel="noopener noreferrer"
  ```
 *
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const {
    children,
    to,
    size = "small",
    replace,
    tooltip,
    onClick,
    color = "turquoise",
    tabIndex,
    openInNewTab = false,
    "aria-label": ariaLabel,
    reloadDocument,
    relative,
  } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedLinkStyles(props);
  const parentLinkContext = useContext(BodyContext);
  const finalSize = size === "inherit" ? (parentLinkContext?.size ?? "medium") : size;
  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Link, "link", color, finalSize],
    testIdPrefix: "link",
  });

  const value = to;
  const url = typeof value === "object" ? value?.pathname : value;
  const isExternalLink = url === undefined ? false : isExternal(url);

  /* If no visible children were provided, fall back to a readable URL label. */
  const visibleTextFallback =
    typeof children === "string" && children.trim().length > 0 ? children : String(url).replace(/^https?:\/\//i, "");

  const contextValue = useMemo(
    (): BodyContextValue => ({
      size: finalSize,
    }),
    [finalSize],
  );

  return (
    <StyleModeBoundary mode={mode}>
      <Tooltip content={tooltip}>
        {(tooltipRef) => (
          <BodyContext.Provider value={contextValue}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <RouterLink
              {...baseHtmlProps}
              onClick={onClick}
              to={to}
              tabIndex={tabIndex}
              aria-label={ariaLabel}
              ref={(elem) => {
                tooltipRef(elem);
                if (ref) {
                  if (typeof ref === "function") ref(elem);
                  else ref.current = elem;
                }
              }}
              replace={replace}
              reloadDocument={reloadDocument}
              target={isExternalLink || openInNewTab ? "_blank" : undefined}
              rel={isExternalLink || openInNewTab ? "noopener noreferrer" : undefined}
              relative={relative}
            >
              {typeof children === "string" && children.trim().length === 0 ? visibleTextFallback : children}
            </RouterLink>
          </BodyContext.Provider>
        )}
      </Tooltip>
    </StyleModeBoundary>
  );
});
